import React, { useState, MouseEvent } from "react";
import {
  Button,
  Menu,
  MenuItem,
  FormControlLabel,
  Switch,
  Collapse,
  Box,
  Typography,
  IconButton,
  useTheme,
} from "@mui/material";
import { TuneOutlined, ExpandMore, ExpandLess } from "@mui/icons-material";
import { makeSxStyles } from "ui";
import useNotificationRecipientsStore from "../../../state/notification-recipients-store";
import { paletteColorStringRenderer } from "../../../shared/utils/string-helpers";

const NotificationRecipientsMapFilters: React.FC = () => {
  const styles = useStyles();
  const { palette } = useTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [showRegionsExpanded, setShowRegionsExpanded] = useState(false);
  const open = Boolean(anchorEl);

  const {
    showAllCustomers,
    showFlatRateOnly,
    showOnDemandOnly,
    showSnowReporters,
    visibleRegions,
    addresses,
    setShowAllCustomers,
    setShowFlatRateOnly,
    setShowOnDemandOnly,
    setShowSnowReporters,
    toggleRegionVisibility,
  } = useNotificationRecipientsStore();

  // Get unique regions from addresses
  const availableRegions = React.useMemo(() => {
    const regions = new Map();
    addresses.forEach((address) => {
      if (address.region) {
        regions.set(address.region.id, address.region);
      }
    });
    return Array.from(regions.values());
  }, [addresses]);

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleToggleRegionsExpanded = () => {
    setShowRegionsExpanded(!showRegionsExpanded);
  };

  // Handle filter changes with proper auto-toggle logic
  const handleFlatRateChange = (checked: boolean) => {
    setShowFlatRateOnly(checked);

    // Update "All customers" based on whether all individual toggles AND all regions are on
    const allCustomerTogglesOn =
      checked && showOnDemandOnly && showSnowReporters;
    const allRegionsOn =
      availableRegions.length > 0 &&
      availableRegions.every((region) => visibleRegions.has(region.id));
    setShowAllCustomers(allCustomerTogglesOn && allRegionsOn);
  };

  const handleOnDemandChange = (checked: boolean) => {
    setShowOnDemandOnly(checked);

    // Update "All customers" based on whether all individual toggles AND all regions are on
    const allCustomerTogglesOn =
      showFlatRateOnly && checked && showSnowReporters;
    const allRegionsOn =
      availableRegions.length > 0 &&
      availableRegions.every((region) => visibleRegions.has(region.id));
    setShowAllCustomers(allCustomerTogglesOn && allRegionsOn);
  };

  const handleSnowReportersChange = (checked: boolean) => {
    setShowSnowReporters(checked);

    // Update "All customers" based on whether all individual toggles AND all regions are on
    const allCustomerTogglesOn =
      showFlatRateOnly && showOnDemandOnly && checked;
    const allRegionsOn =
      availableRegions.length > 0 &&
      availableRegions.every((region) => visibleRegions.has(region.id));
    setShowAllCustomers(allCustomerTogglesOn && allRegionsOn);
  };

  const handleAllCustomersChange = (checked: boolean) => {
    if (checked) {
      // Turn on all individual toggles
      setShowAllCustomers(true);
      setShowFlatRateOnly(true);
      setShowOnDemandOnly(true);
      setShowSnowReporters(true);

      // Turn on all regions
      availableRegions.forEach((region) => {
        if (!visibleRegions.has(region.id)) {
          toggleRegionVisibility(region.id);
        }
      });
    } else {
      // Turn off all toggles
      setShowAllCustomers(false);
      setShowFlatRateOnly(false);
      setShowOnDemandOnly(false);
      setShowSnowReporters(false);

      // Turn off all regions
      availableRegions.forEach((region) => {
        if (visibleRegions.has(region.id)) {
          toggleRegionVisibility(region.id);
        }
      });
    }
  };

  const handleRegionToggle = (regionId: string) => {
    toggleRegionVisibility(regionId);

    // Update "All customers" toggle based on whether all customer toggles AND all regions are on
    const allCustomerTogglesOn =
      showFlatRateOnly && showOnDemandOnly && showSnowReporters;
    const willBeVisible = !visibleRegions.has(regionId); // Toggle will flip the current state

    // Check if all regions will be visible after this toggle
    let allRegionsWillBeOn = true;
    availableRegions.forEach((region) => {
      if (region.id === regionId) {
        allRegionsWillBeOn = allRegionsWillBeOn && willBeVisible;
      } else {
        allRegionsWillBeOn =
          allRegionsWillBeOn && visibleRegions.has(region.id);
      }
    });

    setShowAllCustomers(allCustomerTogglesOn && allRegionsWillBeOn);
  };

  const handleAllRegionsChange = (checked: boolean) => {
    if (checked) {
      availableRegions.forEach((region) => {
        if (!visibleRegions.has(region.id)) {
          toggleRegionVisibility(region.id);
        }
      });
    } else {
      availableRegions.forEach((region) => {
        if (visibleRegions.has(region.id)) {
          toggleRegionVisibility(region.id);
        }
      });
    }
  };

  return (
    <>
      <Button
        sx={styles.menuBoxItem}
        aria-label="filters"
        id="filter-button"
        className={open ? "menuBoxItemActive" : ""}
        onClick={handleClick}
      >
        <TuneOutlined sx={{ color: "#C1C7CE" }} />
      </Button>
      <Menu
        id="filter-menu"
        MenuListProps={{
          "aria-labelledby": "filter-button",
        }}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        className={open ? "onActiveFiltersOpen" : ""}
        sx={styles.filterMenu}
      >
        {/* All Customers Toggle */}
        <MenuItem sx={styles.menuItem}>
          <FormControlLabel
            value="showAllCustomers"
            control={
              <Switch
                checked={showAllCustomers}
                color="primary"
                onChange={(e) => handleAllCustomersChange(e.target.checked)}
              />
            }
            label="All Customers"
            labelPlacement="start"
          />
        </MenuItem>

        {/* Flat Rate Toggle */}
        <MenuItem sx={styles.menuItem}>
          <FormControlLabel
            value="showFlatRateOnly"
            control={
              <Switch
                checked={showFlatRateOnly}
                color="primary"
                onChange={(e) => handleFlatRateChange(e.target.checked)}
              />
            }
            label="Flat Rate"
            labelPlacement="start"
          />
        </MenuItem>

        {/* On Demand Toggle */}
        <MenuItem sx={styles.menuItem}>
          <FormControlLabel
            value="showOnDemandOnly"
            control={
              <Switch
                checked={showOnDemandOnly}
                color="primary"
                onChange={(e) => handleOnDemandChange(e.target.checked)}
              />
            }
            label="On Demand"
            labelPlacement="start"
          />
        </MenuItem>

        {/* Snow Reporters Toggle */}
        <MenuItem sx={styles.menuItem}>
          <FormControlLabel
            value="showSnowReporters"
            control={
              <Switch
                checked={showSnowReporters}
                color="primary"
                onChange={(e) => handleSnowReportersChange(e.target.checked)}
              />
            }
            label="Snow Reporters"
            labelPlacement="start"
          />
        </MenuItem>

        {/* Show Regions - Collapsible (no toggle) */}
        <MenuItem sx={styles.menuItem}>
          <Box sx={styles.regionsContainer}>
            <Box
              sx={styles.regionsHeader}
              onClick={handleToggleRegionsExpanded}
            >
              <Typography variant="body1" sx={styles.regionsHeaderText}>
                Show Regions
              </Typography>
              <IconButton size="small" sx={styles.expandButton}>
                {showRegionsExpanded ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            </Box>

            <Collapse in={showRegionsExpanded}>
              <Box sx={styles.regionsList}>
                <Box sx={styles.regionItem}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={
                          visibleRegions.size === availableRegions.length
                        }
                        color="primary"
                        onChange={(value) =>
                          handleAllRegionsChange(value.target.checked)
                        }
                        size="small"
                      />
                    }
                    label={
                      <Box sx={styles.regionLabel}>
                        <Typography variant="body2">All Regions</Typography>
                      </Box>
                    }
                    labelPlacement="start"
                    sx={styles.regionFormControl}
                  />
                </Box>
                {availableRegions.map((region) => (
                  <Box key={region.id} sx={styles.regionItem}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={visibleRegions.has(region.id)}
                          color="primary"
                          onChange={() => handleRegionToggle(region.id)}
                          size="small"
                        />
                      }
                      label={
                        <Box sx={styles.regionLabel}>
                          <Box
                            sx={{
                              ...styles.regionColorDot,
                              backgroundColor:
                                paletteColorStringRenderer(
                                  palette,
                                  region.color.toLowerCase(),
                                ) || "#666",
                            }}
                          />
                          <Typography variant="body2">{region.name}</Typography>
                        </Box>
                      }
                      labelPlacement="start"
                      sx={styles.regionFormControl}
                    />
                  </Box>
                ))}
                {availableRegions.length === 0 && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={styles.noRegionsText}
                  >
                    No regions available
                  </Typography>
                )}
              </Box>
            </Collapse>
          </Box>
        </MenuItem>
      </Menu>
    </>
  );
};

const useStyles = makeSxStyles(() => ({
  menuBoxItem: {
    width: "36px",
    height: "36px",
    minWidth: "unset",
    padding: "unset",
    borderRadius: "100%",
    color: "#C1C7CE",

    "&:hover": {
      backgroundColor: "rgba(202, 196, 208, 0.12)",
    },

    "&.menuBoxItemActive": {
      textDecoration: "none",
      backgroundColor: "#CAC4D01F !important",
    },

    "&.menuBoxItemActive:hover": {
      backgroundColor: "#CAC4D01F !important",
    },
  },
  filterMenu: {
    "& .MuiPaper-root": {
      minWidth: "280px",
      maxHeight: "400px",
      overflow: "auto",
    },
  },
  menuItem: {
    padding: "8px 16px",
    minHeight: "auto",
    "& .MuiFormControlLabel-root": {
      margin: 0,
      width: "100%",
      justifyContent: "space-between",
    },
  },
  regionsContainer: {
    width: "100%",
  },
  regionsHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
  },
  regionsHeaderText: {
    fontWeight: 500,
  },
  expandButton: {
    padding: "4px",
  },
  regionsList: {
    marginTop: "8px",
    paddingLeft: "16px",
  },
  regionItem: {
    marginBottom: "4px",
  },
  regionFormControl: {
    margin: 0,
    width: "100%",
    "& .MuiFormControlLabel-label": {
      width: "100%",
    },
  },
  regionLabel: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  regionColorDot: {
    width: "12px",
    height: "12px",
    borderRadius: "50%",
    flexShrink: 0,
  },
  noRegionsText: {
    padding: "8px 0",
    fontStyle: "italic",
  },
}));

export default NotificationRecipientsMapFilters;
